import React, { useState } from 'react';
import styled from 'styled-components';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

const Nav = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 80px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 4rem;
  background: white;
  z-index: 1000;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04), 
              0 2px 4px rgba(0, 0, 0, 0.02);

  @media (max-width: 768px) {
    padding: 0 2rem;
  }
`;

const LogoContainer = styled(Link)`
  text-decoration: none;
`;

const LogoText = styled(motion.span)`
  font-size: 1.8rem;
  font-weight: 800;
  background: linear-gradient(135deg, #2563eb 0%, #000000 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  letter-spacing: -0.02em;
`;

const NavItems = styled.div`
  display: flex;
  gap: 3rem;
  align-items: center;

  @media (max-width: 768px) {
    display: none;
  }
`;

const NavItem = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.25rem;

  &:after {
    content: '';
    position: absolute;
    top: 100%;
    left: -20px;
    width: calc(100% + 40px);
    height: 30px;
    background: transparent;
  }
  
  &:hover .services-dropdown,
  .services-dropdown:hover {
    transform: translateY(0);
    opacity: 1;
    pointer-events: auto;
    visibility: visible;
  }
`;

const ServicesDropdown = styled(motion.div)`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  transform: translateY(0);
  width: 600px;
  background: white;
  border-radius: 20px;
  padding: 1.5rem;
  margin-top: 0;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05),
              0 10px 15px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(37, 99, 235, 0.1);
  backdrop-filter: blur(8px);
  z-index: 1000;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  opacity: 0;
  pointer-events: none;
  visibility: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:before {
    content: '';
    position: absolute;
    top: -14px;
    left: 20px;
    transform: translateX(0);
    width: calc(100% + 40px);
    height: 30px;
    background: transparent;
  }

  &:after {
    content: '';
    position: absolute;
    top: -6px;
    left: 20px;
    transform: translateX(0) rotate(45deg);
    width: 12px;
    height: 12px;
    background: white;
    border-left: 1px solid rgba(37, 99, 235, 0.1);
    border-top: 1px solid rgba(37, 99, 235, 0.1);
  }

  @media (max-width: 768px) {
    width: 90vw;
    grid-template-columns: 1fr;
    left: 0;
    transform: translateY(0);
    
    &:hover {
      transform: translateY(0);
    }
  }
`;

const DropdownLink = styled(Link)`
  display: flex;
  flex-direction: column;
  padding: 1.25rem;
  color: #1e293b;
  text-decoration: none;
  border-radius: 12px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid transparent;
  outline: none;

  &:hover, &:focus-visible {
    background: rgba(37, 99, 235, 0.03);
    border-color: rgba(37, 99, 235, 0.1);
    transform: translateY(-2px);
  }

  &:focus-visible {
    box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.5);
  }
`;

const ServiceHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.5rem;
`;

const ServiceIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, rgba(37, 99, 235, 0.1) 0%, rgba(59, 130, 246, 0.1) 100%);
  border-radius: 10px;
  font-size: 1.25rem;
  transition: all 0.2s ease;

  ${DropdownLink}:hover & {
    background: linear-gradient(135deg, rgba(37, 99, 235, 0.15) 0%, rgba(59, 130, 246, 0.15) 100%);
    transform: scale(1.05);
  }
`;

const ServiceTitle = styled.span`
  font-weight: 600;
  font-size: 1rem;
  color: #1e293b;
`;

const ServiceDescription = styled.p`
  font-size: 0.875rem;
  color: #64748b;
  line-height: 1.4;
  margin: 0;
`;

const ChevronIcon = styled(motion.span)`
  display: inline-block;
  font-size: 1rem;
  color: #64748b;
`;

const NavLink = styled(Link)`
  text-decoration: none;
  color: #64748b;
  font-weight: 500;
  font-size: 0.95rem;
  position: relative;
  padding: 0.5rem 0;
  transition: all 0.2s ease;
  letter-spacing: -0.01em;

  &:hover {
    color: #2563eb;
  }

  ${props => props.active && `
    color: #2563eb;
    font-weight: 600;

    &:after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      width: 100%;
      height: 2px;
      background: linear-gradient(135deg, #2563eb 0%, #3b82f6 100%);
      border-radius: 2px;
      transform-origin: center;
      animation: slideIn 0.3s ease forwards;
    }

    @keyframes slideIn {
      from {
        transform: scaleX(0);
      }
      to {
        transform: scaleX(1);
      }
    }
  `}
`;

const ActionButton = styled(Link)`
  background: linear-gradient(135deg, #2563eb 0%, #3b82f6 100%);
  color: white;
  padding: 0.6rem 1.2rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.95rem;
  text-decoration: none;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(37, 99, 235, 0.1);

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 6px rgba(37, 99, 235, 0.2);
  }

  &:active {
    transform: translateY(0);
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  padding: 0.5rem;
  font-size: 1.5rem;
  cursor: pointer;
  color: #64748b;
  transition: color 0.2s ease;

  &:hover {
    color: #2563eb;
  }

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

function Navbar() {
  const location = useLocation();
  const [showServices, setShowServices] = useState(false);

  const services = [
    {
      icon: '🛒',
      title: 'Checkout Testing',
      description: 'Ensure flawless payment processing and cart functionality',
      path: '/services/checkout'
    },
    {
      icon: '⚡',
      title: 'Performance Testing',
      description: 'Optimize speed and handle high traffic with confidence',
      path: '/services/performance'
    },
    {
      icon: '🔄',
      title: 'Integration Testing',
      description: 'Seamless third-party app and API integration testing',
      path: '/services/integration'
    },
    {
      icon: '🔒',
      title: 'Security Testing',
      description: 'Protect your store with comprehensive security checks',
      path: '/services/security'
    },
    {
      icon: '📱',
      title: 'Mobile Testing',
      description: 'Perfect functionality across all mobile devices',
      path: '/services/mobile'
    },
    {
      icon: '🤖',
      title: 'Automation Testing',
      description: 'Streamline testing with intelligent automation',
      path: '/services/automation'
    }
  ];

  return (
    <Nav>
      <LogoContainer to="/">
        <LogoText
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
        >
          Revguard
        </LogoText>
      </LogoContainer>
      <NavItems>
        <NavItem
          onMouseEnter={() => setShowServices(true)}
          onMouseLeave={() => setShowServices(false)}
          onFocus={() => setShowServices(true)}
          onBlur={() => setShowServices(false)}
          role="navigation"
          aria-expanded={showServices}
        >
          <NavLink 
            to="/services" 
            active={location.pathname.includes('/services')}
            aria-haspopup="true"
            aria-expanded={showServices}
          >
            Services
            <ChevronIcon
              animate={{ rotate: showServices ? 180 : 0 }}
              transition={{ duration: 0.3 }}
            >
              ▾
            </ChevronIcon>
          </NavLink>
          <AnimatePresence>
            {showServices && (
              <ServicesDropdown
                className="services-dropdown"
                initial={false}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 15 }}
                transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
                role="menu"
                aria-label="Services menu"
              >
                {services.map((service) => (
                  <DropdownLink 
                    key={service.path} 
                    to={service.path}
                    role="menuitem"
                    tabIndex={0}
                  >
                    <ServiceHeader>
                      <ServiceIcon aria-hidden="true">{service.icon}</ServiceIcon>
                      <ServiceTitle>{service.title}</ServiceTitle>
                    </ServiceHeader>
                    <ServiceDescription>{service.description}</ServiceDescription>
                  </DropdownLink>
                ))}
              </ServicesDropdown>
            )}
          </AnimatePresence>
        </NavItem>
        <NavLink to="/pricing" active={location.pathname === '/pricing'}>
          Pricing
        </NavLink>
        <NavLink to="/faq" active={location.pathname === '/faq'}>
          FAQ
        </NavLink>
        <NavLink to="/contact" active={location.pathname === '/contact'}>
          Contact Us
        </NavLink>
        <ActionButton to="/free-trial">
          Try it Free
        </ActionButton>
      </NavItems>
      <MobileMenuButton aria-label="Menu">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M4 6H20M4 12H20M4 18H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </MobileMenuButton>
    </Nav>
  );
}

export default Navbar;